import React from 'react';
import { styled } from '@mui/material/styles';
import { InView } from 'react-intersection-observer';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Divider,
    ListItem,
    ListItemText,
    Skeleton
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import debounce from 'lodash/debounce';
import {
    IEnrichedCategory,
    IEnrichedMenuWithModifierMaps,
    IEnrichedProduct,
    isProductAvailable,
    isProductGroup,
    ProductGroup
} from './model/Menu';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';
import { MenuProduct } from './MenuProduct';
import { useResponsive } from 'src/hooks/useResponsive';
import { getCompoundKey } from 'lib/helpers';

const PREFIX = 'MenuCategory';

const classes = {
    categoryTitle: `${PREFIX}categoryTitle`,
    panel: `${PREFIX}panel`,
    imageWrapper: `${PREFIX}imageWrapper`,
    image: `${PREFIX}image`,
    details: `${PREFIX}details`,
    products: `${PREFIX}products`
};

const StyledListItem = styled(ListItem)(({ theme }) => ({
    flexDirection: 'column',
    [`& .${classes.categoryTitle}`]: {
        fontWeight: 'bold',
        color: theme.palette.titleTextColour
    },

    [`& .${classes.panel}`]: {
        width: '100%',
        '&::before': {
            height: 0
        },
        marginTop: '0 !important'
    },

    [`& .${classes.imageWrapper}`]: {
        width: '100%',
        position: 'relative',
        // this is for obtaining image ratio of 3x1 (~33.33%)
        paddingTop: `${100 / 3}%`,
        borderRadius: theme.shape.borderRadius,
        overflow: 'hidden'
    },

    [`& .${classes.image}`]: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        position: 'absolute',
        top: 0,
        left: 0
    },

    [`& .${classes.details}`]: {
        padding: 0
    },

    [`& .${classes.products}`]: {
        width: '100%',
        paddingTop: 0
    }
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    alignItems: 'flex-start',
    padding: theme.spacing(0.5, 1.5, 0.5, 1.5)
}));

interface MenuCategoryProps {
    item: IEnrichedCategory;
    isCollapsible?: boolean;
    isExpandedInitially?: boolean;
    menu?: IEnrichedMenuWithModifierMaps;
    onProductSelect: (item: IEnrichedProduct | ProductGroup, categoryId: string) => void;
    onRemove?: (product: IEnrichedProduct | ProductGroup) => void;
    setActiveCategory?: React.Dispatch<React.SetStateAction<string | undefined>>;
}

export const MenuCategory: React.FC<React.PropsWithChildren<MenuCategoryProps>> = ({
    item,
    setActiveCategory,
    isExpandedInitially,
    isCollapsible = true,
    onProductSelect,
    onRemove,
    menu
}) => {
    const { items } = useSelector((state: ApplicationState) => state.basket);
    const { isDesktop } = useResponsive();
    const [expanded, setExpanded] = React.useState(!item.collapsed || isExpandedInitially);
    const [imageLoaded, setImageLoaded] = React.useState(false);
    const togglePanel = React.useCallback(() => setExpanded(isExpanded => !isExpanded), []);
    const handleImageLoad = React.useCallback(() => {
        setImageLoaded(true);
    }, [setImageLoaded]);

    const handleActiveCategory = React.useMemo(
        () =>
            debounce((inView: boolean) => {
                if (inView && setActiveCategory) {
                    setActiveCategory(getCompoundKey(item.groupCategoryId, item.id));
                }
            }, 200),
        [item.groupCategoryId, item.id, setActiveCategory]
    );
    const categoryImage = React.useMemo(
        () =>
            !!item.imageUrl && (
                <Box paddingX={1.5} width="100%">
                    <Box lineHeight={1} onClick={togglePanel} className={classes.imageWrapper}>
                        <img
                            src={item.imageUrl}
                            onLoad={handleImageLoad}
                            alt={item.title}
                            className={classes.image}
                        />
                    </Box>
                    {!imageLoaded && <Skeleton className={classes.imageWrapper} variant="rectangular" />}
                </Box>
            ),
        [handleImageLoad, imageLoaded, item.imageUrl, item.title, togglePanel]
    );

    const handleProductSelect = React.useCallback(
        (product: IEnrichedProduct | ProductGroup) => {
            onProductSelect(product, item.id);
        },
        [item.id, onProductSelect]
    );

    const renderProduct = React.useCallback(
        (product: IEnrichedProduct) => {
            const itemQuantity = items.reduce((acc, basketItem) => {
                if (
                    item.id === basketItem.categoryId &&
                    (product.id === basketItem.productId ||
                        (isProductGroup(product) &&
                            product.products.some(childProduct => childProduct.id === basketItem.productId)))
                ) {
                    return acc + basketItem.quantity;
                }
                return acc;
            }, 0);
            return (
                <MenuProduct
                    key={product.id}
                    item={product}
                    menu={menu}
                    quantity={itemQuantity}
                    onClick={handleProductSelect}
                    isDesktop={isDesktop}
                    onRemove={onRemove}
                />
            );
        },
        [items, menu, handleProductSelect, isDesktop, item.id, onRemove]
    );

    const filteredProducts = React.useMemo(() => {
        if (item.hideUnavailableProducts) {
            return item.products.filter(product => isProductAvailable(product));
        }
        return item.products;
    }, [item.hideUnavailableProducts, item.products]);

    return (
        <StyledListItem disableGutters>
            {isCollapsible ? (
                <>
                    {categoryImage}
                    <Accordion
                        expanded={expanded}
                        onChange={togglePanel}
                        className={classes.panel}
                        slotProps={{
                            transition: {
                                unmountOnExit: true
                            }
                        }}
                        elevation={0}
                    >
                        <StyledAccordionSummary
                            aria-controls={`${item.id}-content`}
                            id={`${item.id}-header`}
                            expandIcon={<ExpandMoreIcon sx={{ fontSize: '32px' }} />}
                            slotProps={{
                                content: {
                                    sx: {
                                        marginTop: 1,
                                        marginBottom: 0
                                    }
                                },
                                expandIconWrapper: {
                                    sx: {
                                        color: 'titleTextColour',
                                        marginLeft: -3,
                                        marginRight: 0,
                                        marginTop: 'auto',
                                        marginBottom: 'auto'
                                    }
                                }
                            }}
                        >
                            <Box width="100%">
                                <ListItemText
                                    slotProps={{
                                        primary: {
                                            className: classes.categoryTitle,
                                            variant: 'h5'
                                        }
                                    }}
                                    primary={item.title}
                                />
                            </Box>
                        </StyledAccordionSummary>
                        <AccordionDetails className={classes.details}>
                            <div className={classes.products}>{filteredProducts.map(renderProduct)}</div>
                        </AccordionDetails>
                    </Accordion>
                </>
            ) : (
                <InView
                    rootMargin={'-110px 0px -80% 0px'}
                    onChange={handleActiveCategory}
                    style={{ width: '100%' }}
                    key={item.id}
                >
                    {categoryImage}
                    <Box width="100%" marginTop={2} marginBottom={1} paddingX={1.5}>
                        <ListItemText
                            primary={item.title}
                            slotProps={{
                                primary: { className: classes.categoryTitle, variant: 'h5' }
                            }}
                        />
                    </Box>
                    <div className={classes.products}>{filteredProducts.map(renderProduct)}</div>
                </InView>
            )}
            <Box paddingX={1.5} width="100%">
                <Divider />
            </Box>
        </StyledListItem>
    );
};
